import { useState, type FormEvent } from "react";
import { LIMITS } from "@ziolo/shared";
import { Button } from "@ziolo/ui";
import { useFormSubmit } from "../hooks/useFormSubmit.js";
import { useSession } from "../session/SessionProvider.js";
import { Field } from "./Field.js";

/**
 * Compact session strip. Login is the default so comments sit high on the page;
 * register is one click away, not a second card.
 */
export function AuthCards() {
  const [mode, setMode] = useState<"login" | "register">("login");

  return mode === "login" ? (
    <LoginStrip onRegister={() => setMode("register")} />
  ) : (
    <RegisterStrip onLogin={() => setMode("login")} />
  );
}

function LoginStrip({ onRegister }: { onRegister: () => void }) {
  const { login } = useSession();
  const { busy, fields, submit } = useFormSubmit();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const ok = await submit(() => login(username, password), "Zalogowano");
    if (ok) setPassword("");
  }

  return (
    <form className="hub-auth" onSubmit={onSubmit}>
      <span className="hub-auth__label">Logowanie</span>
      <div className="hub-auth__fields">
        <Field error={fields.username}>
          <input
            className="z-input"
            name="username"
            autoComplete="username"
            placeholder="Nazwa użytkownika"
            aria-label="Nazwa użytkownika"
            maxLength={LIMITS.username.max}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            required
          />
        </Field>
        <Field error={fields.password}>
          <input
            className="z-input"
            name="password"
            type="password"
            autoComplete="current-password"
            placeholder="Hasło"
            aria-label="Hasło"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </Field>
      </div>
      <div className="hub-auth__actions">
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? "Logowanie..." : "Zaloguj"}
        </Button>
        <button type="button" className="hub-auth__switch" onClick={onRegister}>
          Załóż konto
        </button>
      </div>
    </form>
  );
}

function RegisterStrip({ onLogin }: { onLogin: () => void }) {
  const { register } = useSession();
  const { busy, fields, setFields, submit } = useFormSubmit();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  async function onSubmit(event: FormEvent) {
    event.preventDefault();

    // Checked here rather than server-side: the confirmation box exists to catch typos
    // in this browser, and the API has no use for a second copy of the password.
    if (password !== confirm) {
      setFields({ confirm: "Hasła nie są takie same" });
      return;
    }

    const ok = await submit(
      () => register(username, password),
      "Konto utworzone. Miło Cię widzieć!",
    );
    if (ok) {
      setPassword("");
      setConfirm("");
    }
  }

  return (
    <form className="hub-auth" onSubmit={onSubmit}>
      <span className="hub-auth__label">Rejestracja</span>
      <div className="hub-auth__fields">
        <Field error={fields.username}>
          <input
            className="z-input"
            name="username"
            autoComplete="username"
            placeholder="Nazwa użytkownika"
            aria-label="Nazwa użytkownika"
            maxLength={LIMITS.username.max}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            required
          />
        </Field>
        <Field error={fields.password}>
          <input
            className="z-input"
            name="password"
            type="password"
            autoComplete="new-password"
            placeholder="Hasło"
            aria-label="Hasło"
            minLength={LIMITS.password.min}
            maxLength={LIMITS.password.max}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </Field>
        <Field error={fields.confirm}>
          <input
            className="z-input"
            name="password_confirm"
            type="password"
            autoComplete="new-password"
            placeholder="Powtórz hasło"
            aria-label="Powtórz hasło"
            minLength={LIMITS.password.min}
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            required
          />
        </Field>
      </div>
      <div className="hub-auth__actions">
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? "Zakładanie..." : "Załóż konto"}
        </Button>
        <button type="button" className="hub-auth__switch" onClick={onLogin}>
          Zaloguj
        </button>
      </div>
    </form>
  );
}
